import type { ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import { Text } from '@understory-io/leaf/text';
import { css } from '@understory-io/leaf/css';
import { Link } from 'react-router';
import { Stack } from '@understory-io/leaf/stack';
import { Avatar } from '@understory-io/leaf/avatar';
import { CheckmarkIcon } from './icons/checkmark';
import { markdownStyles } from './markdown-html';

const SAFE_LINK_SCHEMES = /^(https?:|mailto:|tel:)/i;

function safeHref(href: string | undefined): string {
  if (!href) return '#';
  // Allow protocol-relative and root-relative paths (no scheme) plus the
  // explicit allowlist. Reject `javascript:`, `data:`, `vbscript:`, etc.
  if (href.startsWith('/') || href.startsWith('#') || href.startsWith('?')) {
    return href;
  }
  return SAFE_LINK_SCHEMES.test(href) ? href : '#';
}

export function Markdown({
  children,
  options,
  unwrapped,
}: {
  children: string | null;
  options?: { useCheckmarksForLists: boolean };
  /** Leave out the styled wrapper, for `renderMarkdownToHtml`: the HTML goes
   *  into `<MarkdownHtml>`, which brings its own. */
  unwrapped?: boolean;
}) {
  if (!children) return null;

  return (
    <MarkdownWrapper unwrapped={unwrapped}>
      <ReactMarkdown
        remarkPlugins={[remarkBreaks]}
        components={{
          p: ({ node: _node, ...props }) => (
            <Text
              {...props}
              as="p"
              style="body-large-regular"
              color="primary"
            />
          ),
          h2: ({ node: _node, ...props }) => (
            <Text
              {...props}
              as="h2"
              style="subheadline-large"
              color="primary"
            />
          ),
          a: ({ node: _node, href, ...props }) => (
            <Text
              {...props}
              to={safeHref(href)}
              as={Link}
              color="link"
              target="_blank"
              rel="noopener noreferrer"
              style="body-large-regular"
              className={css({
                textDecoration: 'underline',
              })}
            />
          ),
          ul: ({ children, node: _node, ...props }) => (
            <Stack
              {...props}
              as="ul"
              gap={options?.useCheckmarksForLists ? 'medium' : 'tiny'}
            >
              {children}
            </Stack>
          ),
          li: ({ children, node: _node, ...props }) =>
            options?.useCheckmarksForLists ? (
              <Stack
                {...props}
                as="li"
                direction="row"
                gap="medium"
                align={'center'}
                className={css({
                  listStyleType: 'none',
                  margin: 'none',
                })}
              >
                <Avatar
                  size="medium"
                  className={css({
                    flexGrow: 0,
                    flexShrink: 0,
                  })}
                >
                  <CheckmarkIcon size="large-inset" />
                </Avatar>
                <div>{children}</div>
              </Stack>
            ) : (
              <li
                {...props}
                className={css({
                  marginLeft: 'large',
                })}
              >
                {children}
              </li>
            ),
        }}
      >
        {children}
      </ReactMarkdown>
    </MarkdownWrapper>
  );
}

function MarkdownWrapper({
  unwrapped,
  children,
}: {
  unwrapped?: boolean;
  children: ReactNode;
}) {
  return unwrapped ? (
    children
  ) : (
    <div className={markdownStyles}>{children}</div>
  );
}
